import { useQuery } from "@tanstack/react-query";
import { AUDITED_READ_OPTIONS } from "@/shared/api/audited-read";
import { httpClient } from "@/shared/api/http-client";
import { keepRowsWithinScope } from "@/shared/api/scoped-placeholder";
import { INVITATIONS_QUERY_KEY } from "./constants";
import { normalizeOrganizationId } from "@/shared/organizations/organization-id";
import { buildInvitationsQuery } from "./build-invitations-query";
import type { InvitationsListParams } from "./build-invitations-query";
import type { AdminInvitationsList } from "./types";

/**
 * `GET /api/admin/invitations` — invitations across all organizations, newest
 * first. Needs `org.members.read`; the API audits every read (invitee emails
 * are PII) and throttles it to 20/min, so the page never refetches on its own:
 * no polling, no focus refetch and no retry (a retry would eat the budget).
 *
 * The cache entry holds invitee emails, so it is dropped shortly after the
 * view unmounts instead of living for the default five minutes.
 */
export function useInvitationsList(params: InvitationsListParams) {
  const queryKey = [
    ...INVITATIONS_QUERY_KEY,
    params.status ?? "all",
    params.role ?? "all",
    normalizeOrganizationId(params.organizationId) ?? "all",
    params.page,
  ] as const;
  return useQuery({
    queryKey,
    queryFn: () =>
      httpClient<AdminInvitationsList>(`/api/admin/invitations${buildInvitationsQuery(params)}`),
    // Keep the previous rows while paging, but never show rows of another
    // filter as if they were the result of the new one.
    placeholderData: keepRowsWithinScope<AdminInvitationsList>(queryKey),
    ...AUDITED_READ_OPTIONS,
  });
}
